import React, { useContext, useState } from "react";
import { PatientContext, ThemeContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import invitePatientSchema from "./validations/invitePatientSchema";

import theme from "../../index.scss";
import LabelledInput from "../UIComponents/LabelledInput";
import SubmitButton from "../UIComponents/SubmitButton";

import {
  sendPatientEmail,
  sendPatientEmail2,
  sendFreeTrialPatientInvite,
  checkPatientEmailExisting,
} from "../../api/helpReq";

const InvitePatient = ({
  user,
  hideTrigger = false,
  visible,
  setVisible,
  isFreeTrialInvite = false,
}) => {
  const { dark } = useContext(ThemeContext);
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [inputVisibleState, setInputVisibleState] = useState(false);
  // Allows a parent (e.g. PatientControl's "Request Invitation from Patient"
  // button) to control visibility instead of the built-in header toggle.
  const inputVisible = visible !== undefined ? visible : inputVisibleState;
  const setInputVisible = setVisible || setInputVisibleState;
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [
    existingProviderCaregiverAccount,
    setExistingProviderCaregiverAccount,
  ] = useState(false);
  const [existingPatientAccount, setExistingPatientAccount] = useState(false);
  const { register, handleSubmit, errors, setError } = useForm({
    resolver: yupResolver(invitePatientSchema),
  });

  const onSubmit = async (data) => {
    setSubmitDisabled(true);
    data.patientEmail = data.patientEmail.toLowerCase();

    // Free-trial flow: just send the invite email, no existing-account
    // check and no patientId (PatientControl has no patient selected yet).
    if (isFreeTrialInvite) {
      try {
        await sendFreeTrialPatientInvite({ data });
        setEmailSent(true);
        setInputVisible(false);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setSubmitDisabled(false);
      }
      return;
    }

    if (user.userType === "Therapist" || user.userType === "provider") {
      try {
        const res = await checkPatientEmailExisting({
          data,
          patientId: patient?.patientId,
        });

        if (res.data.exist === "false") {
          if (user.userType === "Therapist") {
            await sendPatientEmail({
              data,
              patientId: patient?.patientId,
              userType: user.userType,
            });
          } else if (user.userType === "provider") {
            await sendPatientEmail2({
              data,
              patientId: patient?.patientId,
              userType: user.userType,
            });
          }
          setEmailSent(true);
          setInputVisible(false);
        } else if (res.data.exist === "true") {
          setExistingPatientAccount(true);
        } else if (res.data.exist === "existingOtherTypeAccount") {
          setExistingProviderCaregiverAccount(true);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setSubmitDisabled(false);
      }
    }
  };

  return (
    <>
      {!hideTrigger && (
        <div className="d-flex justify-content-center">
          <h6
            className="pt-2 pb-2 pl-3 pr-3"
            style={{
              width: "75%",
              borderRadius: "20px",
              backgroundColor: dark ? theme.darkModePrimary : "#f7bc12",
              color: dark ? theme.darkModeText : "white",
              textAlign: "center",
              overflowWrap: "break-word",
              cursor: "pointer",
            }}
            onClick={() => setInputVisible(!inputVisible)}
          >
            {t("invitepatient.header")}
          </h6>
        </div>
      )}
      {inputVisible && (
        <Row className="p-2">
          <Col>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="p-2">
                <Col>
                  <LabelledInput
                    required
                    label={t("invitepatient.patient_email")}
                    name="patientEmail"
                    type="email"
                    errors={errors}
                    inputRef={register}
                  />
                  <SubmitButton type="submit" disabled={submitDisabled}>
                    {t("invitepatient.button")}
                  </SubmitButton>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      )}
      {emailSent && (
        <Row className="p-2">
          <Col className="text-center">
            {t("invitepatient.messages.sent")}
          </Col>
        </Row>
      )}
      {existingProviderCaregiverAccount && (
        <Row className="p-2">
          <Col className="text-center">
            {t("invitepatient.messages.other_account_type")}
          </Col>
        </Row>
      )}
      {existingPatientAccount && (
        <Row className="p-2">
          <Col className="text-center">
            {t("invitepatient.messages.existing_patient_account")}
          </Col>
        </Row>
      )}
    </>
  );
};

export default InvitePatient;